import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import moment from 'moment';

import {
  getCatalogComparePairDetail,
  getCatalogComparePairs,
  getCatalogVariant,
  getCatalogVariants
} from '../../api/catalogs';
import { catalogVisibilityBadge, CatalogVariantCard, CatalogVariantGrid, CatalogVariantPanel, useDeferredResizeMeasure } from './CatalogVariantGrid';
import CatalogCompareGrid, { catalogChangeNotes, catalogComparePairsPerRow, CATALOG_CHANGE_LABELS, CHANGE_TONES } from './CatalogCompareGrid';
import CatalogPagination from './CatalogPagination';
import './CurrentCatalog.scss';

const GRID_MIN_CARD_WIDTH = 200;
const GRID_GAP = 12;
const GRID_ROWS_PER_PAGE = 4;

const COMPARE_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'changed', label: 'All changes' },
  ...Object.entries(CATALOG_CHANGE_LABELS).map(([key, label]) => ({ key, label })),
  { key: 'unchanged', label: 'Unchanged' }
];

const VISIBILITY_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'visible', label: 'Visible' },
  { key: 'hidden', label: 'Hidden' }
];

// Compare must default to every visibility: a draft that flips a variant to
// hidden classifies as hidden, so a 'visible' default would drop exactly the
// pairs a pre-publish review exists to catch.
const MODE_DEFAULT_VISIBILITY = { current: 'visible', compare: 'all', proposed: 'visible' };

const CurrentCatalog = ({ brandKey, BrandIntegration_id, currentLabel = 'Current', draftStamp = null, isDraftReady = false, Run_id = null, versionLabel = 'Draft' }) => {
  const [mode, setMode] = useState(isDraftReady ? 'compare' : 'current');
  const [rows, setRows] = useState([]);
  const [compareContext, setCompareContext] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchValue, setSearchValue] = useState('');
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState('all');
  const [visibility, setVisibility] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const detailKeyRef = useRef(null);
  const rootRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(null);
  const effectiveVisibility = visibility ?? MODE_DEFAULT_VISIBILITY[mode];
  const comparePairsPerRow = gridWidth === null ? null : catalogComparePairsPerRow(gridWidth);
  const cardsPerRow = gridWidth === null ? null : Math.max(1, Math.floor((gridWidth + GRID_GAP) / (GRID_MIN_CARD_WIDTH + GRID_GAP)));
  const gridPageSize = gridWidth === null ? null : GRID_ROWS_PER_PAGE * (mode === 'compare' ? comparePairsPerRow : cardsPerRow);
  const catalogModes = [
    { key: 'current', label: currentLabel },
    { key: 'compare', label: 'Compare' },
    { key: 'proposed', label: versionLabel }
  ];

  useEffect(
    () => () => {
      detailKeyRef.current = null;
    },
    []
  );

  useDeferredResizeMeasure(rootRef, node => {
    const width = node?.clientWidth || 0;
    setGridWidth(previous => {
      if (width || previous === null) return width;
      return previous;
    });
  });

  useEffect(() => {
    const nextQuery = searchValue.trim();
    if (nextQuery === query) return undefined;
    const timeout = setTimeout(() => {
      setPage(0);
      closeDetail();
      setQuery(nextQuery);
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, searchValue]);

  useEffect(() => {
    if (gridPageSize === null) return undefined;
    if (mode !== 'current' && !isDraftReady) {
      setRows([]);
      setTotal(0);
      setCompareContext(null);
      setIsLoading(false);
      setError(null);
      return undefined;
    }
    let isActive = true;
    setIsLoading(true);
    setError(null);
    const gridParams = gridWidth ? { pageSize: gridPageSize } : {};
    const request =
      mode === 'current'
        ? getCatalogVariants(brandKey, { BrandIntegration_id, page, query, visibility: effectiveVisibility, ...gridParams })
        : getCatalogComparePairs(brandKey, {
            BrandIntegration_id,
            page,
            query,
            filter: mode === 'proposed' ? 'proposed' : chip,
            visibility: effectiveVisibility,
            ...(Run_id ? { Run_id } : {}),
            ...gridParams
          });
    request
      .then(result => {
        if (!isActive) return;
        setRows(mode === 'current' ? result.variants : result.pairs);
        setTotal(result.total);
        setPageSize(result.pageSize);
        setCompareContext(
          mode === 'current'
            ? null
            : { ...result.summary, generatedAt: result.generatedAt, captureKey: result.captureKey }
        );
      })
      .catch(requestError => {
        if (isActive) setError(requestError);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [BrandIntegration_id, brandKey, isDraftReady, mode, page, query, chip, effectiveVisibility, draftStamp, gridPageSize, Run_id]);

  const seenGridPageSize = useRef(null);
  useEffect(() => {
    if (seenGridPageSize.current !== null && gridPageSize !== seenGridPageSize.current) {
      setPage(0);
      closeDetail();
    }
    seenGridPageSize.current = gridPageSize;
  }, [gridPageSize]);

  const closeDetail = () => {
    detailKeyRef.current = null;
    setExpandedKey(null);
    setDetail(null);
    setDetailError(null);
  };

  const resetList = () => {
    setRows([]);
    setTotal(0);
    setPage(0);
    setIsLoading(true);
    setError(null);
    closeDetail();
  };

  const changeMode = nextMode => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setCompareContext(null);
    resetList();
  };

  const seenDraftStamp = useRef(draftStamp);
  useEffect(() => {
    if (draftStamp && draftStamp !== seenDraftStamp.current) {
      setMode('compare');
      setChip('all');
      setVisibility(null);
      setPage(0);
      closeDetail();
    }
    seenDraftStamp.current = draftStamp;
  }, [draftStamp]);

  const changeChip = nextChip => {
    if (nextChip === chip) return;
    setChip(nextChip);
    setPage(0);
    closeDetail();
  };

  const changeVisibility = nextVisibility => {
    if (nextVisibility === effectiveVisibility) return;
    setVisibility(nextVisibility);
    setPage(0);
    closeDetail();
  };

  const changePage = nextPage => {
    setPage(nextPage);
    closeDetail();
  };

  const submitSearch = event => {
    event.preventDefault();
    setPage(0);
    closeDetail();
    setQuery(searchValue.trim());
  };

  const toggleExpanded = key => {
    if (expandedKey === key) {
      closeDetail();
      return;
    }
    const token = `${mode}:${key}`;
    detailKeyRef.current = token;
    setExpandedKey(key);
    setDetail(null);
    setDetailError(null);
    setIsDetailLoading(true);
    const request = mode === 'current'
      ? getCatalogVariant(brandKey, BrandIntegration_id, key)
      : getCatalogComparePairDetail(brandKey, BrandIntegration_id, key, Run_id);
    request
      .then(result => {
        if (detailKeyRef.current === token) setDetail(result);
      })
      .catch(requestError => {
        if (detailKeyRef.current === token) setDetailError(requestError);
      })
      .finally(() => {
        if (detailKeyRef.current === token) setIsDetailLoading(false);
      });
  };

  const pages = Math.ceil(total / pageSize);

  const rowKey = item => (mode === 'current' ? item.variantKey : item.pairKey);

  const renderPanel = () => {
    const pair = mode === 'current' ? null : rows.find(row => row.pairKey === expandedKey);
    const card = mode === 'current' ? rows.find(row => row.variantKey === expandedKey) : pair?.proposed || pair?.current;
    const name = detail ? (mode === 'current' ? detail.name : detail.name.proposed || detail.name.current) : card?.name;
    return (
      <CatalogVariantPanel
        badges={panelBadges(mode, pair, detail, card)}
        error={detailError}
        imageUrl={detail?.imageUrl || card?.imageUrl}
        isLoading={isDetailLoading || (!detail && !detailError)}
        name={name}
        notes={pair ? catalogChangeNotes(pair, pair.proposed ? 'proposed' : 'current') : []}
        onClose={closeDetail}
        previousName={mode === 'compare' && detail?.changeTypes?.includes('renamed') ? detail.name.current : null}
        productType={detail?.productType || card?.productType}
        siblings={detail?.siblings || []}
        sources={detail?.sources || detail?.sourceListings || []}
      />
    );
  };

  return (
    <div ref={rootRef}>
      <div className='catalogs-toolbar'>
        <div className='catalogs-segments'>
          {catalogModes.map(item => (
            <button className={cn({ active: mode === item.key })} key={item.key} onClick={() => changeMode(item.key)}>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className='catalogs-viewer-tools'>
        <form className='catalogs-search' onSubmit={submitSearch}>
          <input value={searchValue} onChange={event => setSearchValue(event.target.value)} placeholder='Search names, categories, tags, SKUs, URLs…' />
          <button type='submit'>Search</button>
        </form>
        <div className='catalogs-chip-group'>
          <span className='catalogs-chip-group-label'>Visibility</span>
          <div className='catalogs-chip-group-row'>
            <VisibilityChips onChange={changeVisibility} visibility={effectiveVisibility} />
          </div>
        </div>
        {mode === 'compare' && compareContext && (
          <div className='catalogs-chip-group'>
            <span className='catalogs-chip-group-label'>Change type</span>
            <div className='catalogs-chip-group-row'>
              {COMPARE_CHIPS.map(item => {
                const count = chipCount(compareContext, item.key);
                if (!count && !['all', 'changed', 'unchanged'].includes(item.key)) return null;
                return (
                  <button className={cn('catalogs-compare-chip', { active: chip === item.key })} key={item.key} onClick={() => changeChip(item.key)}>
                    {item.label} <b>{count.toLocaleString()}</b>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className='catalogs-result-count'>
          {compareContext
            ? compareContext.changed
              ? `${compareContext.changed.toLocaleString()} changed · ${compareContext.unchanged.toLocaleString()} unchanged`
              : `No catalog changes · ${compareContext.unchanged.toLocaleString()} unchanged`
            : `${total.toLocaleString()} results`}
        </div>
      </div>

      {mode === 'proposed' && !query && effectiveVisibility === 'all' && compareContext?.changed === 0 && (
        <div className='catalogs-proposed-banner'>{`${versionLabel} is identical to ${currentLabel.toLowerCase()}.`}</div>
      )}

      {mode !== 'current' && !isDraftReady ? (
        <div className='catalogs-empty compact'>
          {mode === 'compare' ? 'Create a draft catalog to compare.' : 'Create a draft catalog to see it.'}
        </div>
      ) : error ? (
        <div className='catalogs-error'>{error}</div>
      ) : isLoading ? (
        <div className='catalogs-empty'>
          {mode === 'current' ? 'Loading the current catalog…' : mode === 'proposed' ? 'Loading the proposed catalog…' : 'Comparing to the live catalog…'}
        </div>
      ) : !rows.length ? (
        <div className='catalogs-empty compact'>
          {mode === 'compare' && chip === 'changed' && !query
            ? `${currentLabel} already matches ${versionLabel.toLowerCase()}.`
            : 'No variants match this view.'}
        </div>
      ) : mode === 'compare' ? (
        <CatalogCompareGrid
          currentLabel={catalogLabel(currentLabel)}
          expandedKey={expandedKey}
          onToggle={pair => toggleExpanded(pair.pairKey)}
          pairs={rows}
          pairsPerRow={gridWidth ? comparePairsPerRow : undefined}
          renderPanel={renderPanel}
          versionLabel={catalogLabel(versionLabel)}
        />
      ) : (
        <CatalogVariantGrid
          cardsPerRow={cardsPerRow}
          expandedKey={expandedKey}
          items={rows}
          keyOf={rowKey}
          renderCard={item => {
            const variant = mode === 'current' ? item : item.proposed;
            return (
              <CatalogVariantCard
                badges={[catalogVisibilityBadge(variant)].filter(Boolean)}
                isExpanded={expandedKey === rowKey(item)}
                key={rowKey(item)}
                onToggle={() => toggleExpanded(rowKey(item))}
                variant={variant}
              />
            );
          }}
          renderPanel={renderPanel}
        />
      )}

      {!isLoading && !error && pages > 1 && <CatalogPagination onPage={changePage} page={page} pages={pages} />}

      {compareContext && (
        <div className='catalogs-muted catalogs-compare-footnote'>
          {versionLabel} · snapshot {compareContext.generatedAt ? `captured ${moment(compareContext.generatedAt).format('MMM D, YYYY h:mm A')}` : compareContext.captureKey} · this
          comparison writes nothing.
        </div>
      )}
    </div>
  );
};

const VisibilityChips = ({ onChange, visibility }) => (
  <>
    {VISIBILITY_CHIPS.map(item => (
      <button className={cn('catalogs-compare-chip', { active: visibility === item.key })} key={item.key} onClick={() => onChange(item.key)}>
        {item.label}
      </button>
    ))}
  </>
);

const chipCount = (summary, key) => {
  if (key === 'all') return summary.changed + summary.unchanged;
  if (key === 'changed') return summary.changed;
  if (key === 'unchanged') return summary.unchanged;
  return summary.byType?.[key] || 0;
};

const panelBadges = (mode, pair, detail, card) => {
  const changeType = mode === 'compare' ? detail?.changeType || pair?.changeType : null;
  return [
    changeType === 'visibility' ? null : catalogVisibilityBadge(card),
    changeType ? { label: CATALOG_CHANGE_LABELS[changeType], tone: CHANGE_TONES[changeType] } : null
  ].filter(Boolean);
};

const catalogLabel = label => String(label).replace(/(\s*\(#\d+\))?$/, ' Catalog$1');

export default CurrentCatalog;
